import type { ReactNode } from "react"
import { CircleAlert, CircleX, Loader } from "lucide-react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"

interface Props {
  isLoading?: boolean
  loadingMessage?: string
  loadingContent?: ReactNode
  
  isEmpty?: boolean
  emptyMessage?: string
  emptyContent?: ReactNode

  isError?: boolean
  errorMessage?: string
  errorContent?: ReactNode

  children?: ReactNode
  className?: string
}

/**
* Unified loading/error/empty state for non-table areas (cards, details, charts).
* Fetches its own UI text from `common:table.*` (like ConfirmDialog/PaginationFooter).
* Order follows the portal-wide standard: isLoading → isError → isEmpty → content. 
* For tables, there is a separate `LoaderTable` component that wraps these same states within TableRow/TableCell elements. 
*/
export function LoaderGeneral({
  isLoading,
  loadingMessage,
  loadingContent,
  isEmpty,
  emptyMessage,
  emptyContent,
  isError,
  errorMessage,
  errorContent,
  children,
  className
}: Props) {
  const { t } = useTranslation('common')

  if (isLoading) {
    if (loadingContent !== undefined) return <>{loadingContent}</>
    return (
      <div className={cn("flex items-center justify-center gap-2 py-8 text-muted-foreground", className)}>
        <Loader className="size-4 animate-spin text-primary" />
        {loadingMessage ?? t('table.loading')}
      </div>
    )
  }
  
  if (isEmpty) {
    if (emptyContent !== undefined) return <>{emptyContent}</>
    return (
      <div className={cn("flex items-center justify-center gap-2 py-8 text-muted-foreground", className)}>
        <CircleAlert className="size-4"/>
        {emptyMessage ?? t('table.empty')}
      </div>
    )
  }

  if (isError) {
    if (errorContent !== undefined) return <>{errorContent}</>
    return (
      <div className={cn("flex items-center justify-center gap-2 py-8 text-muted-foreground", className)}>
        <CircleX className="size-4"/>
        {errorMessage ?? t('table.loadError')}
      </div>
    )
  }


  return (<>{children}</>)
}
